import { ExternalScheduleConcreteDirectInputsProps } from './types';
import { FormField } from '@components/ui/form';
import { FormItemWrapper } from '@components/ui/form/itemWrapper';
import { useTranslation } from 'react-i18next';
import { Input } from '@components/ui/form/input';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio';
import { MultiSelect } from '@components/ui/form/multiSelect';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useFormContext } from 'react-hook-form';
import { SelectInput } from '@components/ui/form/select/input';
import { ExternalScheduleFormValues } from '@forms/schedules/external/types';

export const ExternalScheduleConcreteDirectInputs = ({
  disabled,
  address,
  geolocalization,
  children,
}: ExternalScheduleConcreteDirectInputsProps) => {
  const { sites, pourMethods, pourElements, concreteMix, additive, rateTypes } =
    useDropdownsContext();

  const { control, watch, setValue } = useFormContext<ExternalScheduleFormValues>();

  const { t } = useTranslation('app', { keyPrefix: 'forms' });

  const { rateType, additive: additiveValue } = watch();

  return (
    <>
      <FormField
        control={control}
        name="externalSiteId"
        render={({ field: { value, onChange } }) => (
          <FormItemWrapper label={t('externalSiteId.label')} select required>
            <SelectInput
              name="externalSiteId"
              value={value || ''}
              onChange={onChange}
              options={sites.options.map((el) => ({ name: el.siteName, value: el.externalSiteId }))}
              disabled={sites.disabled || disabled}
              placeholder={t('default.select.placeholder')}
            />
          </FormItemWrapper>
        )}
      />
      <FormItemWrapper label={t('address.label')}>
        <Input readOnly disabled value={address} />
      </FormItemWrapper>
      <div className="flex gap-4">
        <FormItemWrapper label={t('geolocalization.label')}>
          <Input readOnly disabled value={geolocalization} />
        </FormItemWrapper>
      </div>
      {children}
      <div className="flex gap-4">
        <FormField
          control={control}
          name="id"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <FormItemWrapper label={t('itemExternalId.label')} select required>
              <MultiSelect
                className="font-normal"
                error={error}
                value={value || []}
                onChange={(v) => {
                  onChange(v);
                  const selected = concreteMix.options.find((el) => el.id === v[0]);
                  if (!selected) return;
                  setValue('itemExternalId', selected.itemExternalId);
                  setValue('slump', selected?.defaultSlump || '');
                }}
                placeholder={t('default.select.placeholder')}
                disabled={concreteMix.disabled || disabled}
                singleMode
                options={concreteMix.options.map((el) => ({
                  value: el.id,
                  label: el.description,
                }))}
              />
            </FormItemWrapper>
          )}
        />
      </div>
      <div className="flex items-start gap-4">
        <FormField
          control={control}
          name="quantity"
          render={({ field, fieldState: { error } }) => (
            <FormItemWrapper className="grow" label={t('quantity.label')} required>
              <Input
                error={error?.message}
                type="number"
                className="w-full"
                disabled={disabled}
                rightAlignValue
                suffixElement={<strong>cy</strong>}
                {...field}
              />
            </FormItemWrapper>
          )}
        />
        <div className="flex grow items-end gap-4">
          <FormField
            control={control}
            name="isExactQuantity"
            render={({ field: { value, onChange } }) => (
              <RadioGroup
                name="isExactQuantity"
                className="flex h-8 items-center gap-3 text-xs"
                defaultValue={value ? 'exact' : 'estimated'}
                disabled={disabled}
                onValueChange={(v) => onChange(v === 'exact')}>
                <span className="flex items-center gap-2">
                  <RadioGroupItem id="exact" value="exact" />
                  <label htmlFor="exact">{t('exact.label')}</label>
                </span>
                <span className="flex items-center gap-2">
                  <RadioGroupItem id="estimated" value="estimated" />
                  <label htmlFor="estimated">{t('estimated.label')}</label>
                </span>
              </RadioGroup>
            )}
          />
          <FormField
            control={control}
            name="slump"
            render={({ field }) => (
              <FormItemWrapper label={t('estimatedSlump.label')}>
                <Input type="number" disabled={disabled} className="w-32" {...field} />
              </FormItemWrapper>
            )}
          />
        </div>
      </div>
      <FormField
        control={control}
        name="additive.id"
        render={({ field: { value, onChange } }) => (
          <FormItemWrapper label={t('additive.label')} select required>
            <MultiSelect
              className="font-normal"
              value={value}
              onChange={(v) => {
                onChange(v);
                const selected = additive.options.find((el) => el.id === v[0]);
                setValue('additive.itemExternalId', selected?.itemExternalId || '');
                if (!selected) setValue('additive.quantity', '');
              }}
              placeholder={t('default.select.placeholder')}
              disabled={additive.disabled || disabled}
              singleMode
              options={additive.options.map((el) => ({
                value: el.id,
                label: el.description,
              }))}
            />
          </FormItemWrapper>
        )}
      />
      {additiveValue?.itemExternalId && (
        <FormField
          control={control}
          name="additive.quantity"
          render={({ field, fieldState: { error } }) => (
            <FormItemWrapper className="basis-full" label={t('quantity.label')} required>
              <Input
                error={error?.message}
                type="number"
                disabled={disabled}
                className="w-full"
                {...field}
              />
            </FormItemWrapper>
          )}
        />
      )}

      <div className="flex gap-4">
        <FormField
          control={control}
          name="pourMethod"
          render={({ field: { value, onChange } }) => (
            <FormItemWrapper label={t('pourMethod.label')} select required>
              <SelectInput
                name="pourMethod"
                value={value || ''}
                onChange={onChange}
                options={pourMethods.options}
                disabled={pourMethods.disabled || disabled}
                placeholder={t('default.select.placeholder')}
              />
            </FormItemWrapper>
          )}
        />
        <FormField
          control={control}
          name="pourElement"
          render={({ field: { value, onChange } }) => (
            <FormItemWrapper label={t('pourElement.label')} select required>
              <SelectInput
                name="pourElement"
                value={value || ''}
                onChange={onChange}
                options={pourElements.options}
                disabled={pourElements.disabled || disabled}
                placeholder={t('default.select.placeholder')}
              />
            </FormItemWrapper>
          )}
        />
      </div>
      <div className="flex gap-4">
        <FormField
          control={control}
          name="rateType"
          render={({ field: { value, onChange } }) => (
            <FormItemWrapper label={t('rateType.label')} select>
              <SelectInput
                name="rateType"
                value={value || ''}
                onChange={onChange}
                options={rateTypes.options.map((el) => ({ name: t(`rateType.${el}`), value: el }))}
                disabled={rateTypes.disabled || disabled}
                placeholder={t('default.select.placeholder')}
              />
            </FormItemWrapper>
          )}
        />
        <FormField
          control={control}
          name="quantityRate"
          render={({ field, fieldState: { error } }) => (
            <FormItemWrapper label={t('quantity.label')} required>
              <Input
                error={error?.message}
                type="number"
                className="w-full"
                disabled={disabled}
                rightAlignValue
                suffixElement={<strong>{rateType === 'QTY_PER_HOUR' ? 'hours' : 'mins'}</strong>}
                {...field}
              />
            </FormItemWrapper>
          )}
        />
      </div>
      <FormField
        control={control}
        name="additionalInformation"
        render={({ field }) => (
          <FormItemWrapper label={t('additionalInfo.label')}>
            <Input disabled={disabled} {...field} />
          </FormItemWrapper>
        )}
      />
      <FormField
        control={control}
        name="note"
        render={({ field }) => (
          <FormItemWrapper label={t('internalNote.label')}>
            <Input disabled={disabled} {...field} />
          </FormItemWrapper>
        )}
      />
    </>
  );
};
